import Vue from 'vue';
import Vuex from 'vuex';
import { message } from 'ant-design-vue';
import { setStorage } from '@/utils';
import {
  resolveTheme,
  applyTheme,
  setTheme as applyAndStoreTheme,
  toggleTheme as toggleStoredTheme,
} from '@/utils/theme';
import { Network, activeChains, defaultChain } from '@/config/constants';
import { CHAIN_REGISTRY, toHexChainId } from '@/config/chains';
import { ensureWalletChain } from '@/wallet/chain';
// RPC 韧性层：只读节点跟随当前链（Deposit/Withdraw 的 429 防护）
import { setActiveChainId } from '@/sdk/rpc';
import { ethers } from 'ethers';
import { getUserInfo, chainList, riskStatus } from '@/api';
import i18n from '@/i18n/index';
import moment from 'moment';
import 'moment/locale/zh-cn';
let loadingGetUserInfo = false;

/**
 * 当前选中链（多链切换的唯一状态源）。
 * 持久化在 localStorage：刷新/重开页面保持在用户选的链上；
 * 值非法（链被下线）时回落到注册表默认链，避免落到一条没有数据的链。
 */
const CHAIN_STORAGE_KEY = 'predi-chain-id';

function resolveDesireChainId() {
  try {
    const saved = localStorage.getItem(CHAIN_STORAGE_KEY);
    if (saved && Network[saved]) {
      return saved;
    }
  } catch (e) {
    /* 隐私模式下 localStorage 可能抛错，忽略 */
  }
  return toHexChainId(defaultChain.chainId);
}

Vue.use(Vuex);
export default new Vuex.Store({
  state: {
    fundData: [
      {
        balance: 0,
      },
    ],
    statics: {
      cash: 0,
      portfolio: 0,
    },
    device: 'desktop',
    account: null,
    chainId: null,
    // 十六进制 chainId，默认 = 注册表默认链（BSC）
    desireChainId: resolveDesireChainId(),
    // 主题：'night' | 'day'，单一数据源由 @/utils/theme 维护
    currentTheme: resolveTheme(),
    // v2.4.0：后台下发的链顺序（CHAIN_REGISTRY 的 key 数组）。
    // 后台排序规则 = 主网在前、测试网在后，组内按 sort；运营可随时调。
    // 为空表示还没拉到 /api/chains，此时沿用构建期 VUE_APP_CHAINS 的顺序。
    chainOrder: [],
    // v2.4.0：后台「排名第一的启用链」= 默认链（新用户首次打开落在这里）。
    serverDefaultKey: '',
    // 每条链的测试网标记（chainId -> boolean），同样来自 /api/chains
    testnetMap: {},
    web3model: null,
    // v2.5.0 资金风控：提现 / 站内转账 / 交易三个开关（后端 /api/risk/status）。
    // 默认全开 —— 拉不到接口时按「正常」处理，不能让网络问题把功能锁死。
    risk: {
      withdraw_enabled: true,
      transfer_enabled: true,
      trade_enabled: true,
      withdraw_note: '',
      transfer_note: '',
      trade_note: '',
    },
  },
  getters: {
    /**
     * 可切换的链列表（顶部切换器 / 钱包弹窗用），只含 .env 里启用的链。
     *
     * v2.4.0：顺序不再由构建期的 VUE_APP_CHAINS 决定 —— 拉到 /api/chains 后
     * 按后台顺序重排（主网在前、测试网在后，组内按后台 sort），
     * 这样运营在后台调完顺序，用户侧立刻生效，不用重新构建前端。
     */
    chainOptions(state) {
      const list = activeChains.slice();
      const order = state.chainOrder || [];
      if (order.length) {
        list.sort((a, b) => {
          const ia = order.indexOf(a.key);
          const ib = order.indexOf(b.key);
          // 后台没下发的链（前端单独启用）排在最后，保持原相对顺序
          return (ia < 0 ? 999 : ia) - (ib < 0 ? 999 : ib);
        });
      }
      const fallbackDefault = state.serverDefaultKey || defaultChain.key;
      return list.map((c) => ({
        key: c.key,
        chainId: c.chainId,
        hex: toHexChainId(c.chainId),
        name: c.chainName,
        shortName: c.chainNameMobile || c.chainName,
        icon: c.icon,
        enum: c.chainEnum,
        // 以后台标记为准，拉不到接口时退回「名字里带 test」的老判断
        isTestnet: Object.prototype.hasOwnProperty.call(state.testnetMap || {}, c.chainId)
          ? !!state.testnetMap[c.chainId]
          : /test/i.test(c.chainName),
        isDefault: c.key === fallbackDefault,
      }));
    },
    /** 当前选中链的注册表项 */
    activeChain(state) {
      const hex = state.desireChainId;
      const byHex = activeChains.find((c) => toHexChainId(c.chainId) === hex);
      return byHex || defaultChain;
    },
    chainType(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return null;
      }
      return Network[chainId]['chainType'];
    },
    payToken(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return {};
      }
      console.log(Network[chainId])
      return Network[chainId]['payToken'];
    },
    /** 后端 Chain 枚举名（BSC 等）：签名消息里必须用它，不能用展示名 */
    chainEnum(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return null;
      }
      return Network[chainId]['chainEnum'] || 'BSC';
    },
    chainName(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return {};
      }
      return Network[chainId]['chainName'];
    },

    ferc20(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return {};
      }
      return Network[chainId]['ferc20'];
    },
    isConnected(state) {
      if (!state.chainId) {
        return false;
      }
      return state.chainId == state.desireChainId;
    },
    explorer(state) {
      let chainId = state.desireChainId;
      if (!chainId) {
        return null;
      }
      // 新链接入初期可能还没有区块浏览器（如 BOT），此时返回 null 由调用方降级
      let urls = Network[chainId]['blockExplorerUrls'];
      return Array.isArray(urls) && urls.length ? urls[0] : null;
    },
  },
  mutations: {
    // v2.5.0：风控开关快照（后端为准，前端只负责置灰 / 提示）
    SET_RISK: (state, risk) => {
      if (risk && typeof risk === 'object') {
        state.risk = { ...state.risk, ...risk };
      }
    },
    SET_ACCOUNT: (state, account) => {
      if (account) {
        account = ethers.utils.getAddress(account);
      }
      state.account = account;
    },
    Set_Web3modal(state, value) {
      state.web3model = value;
    },
    SET_CHAINID: (state, id) => {
      state.chainId = id;
    },
    SET_FUNDData: (state, data) => {
      state.fundData = data;
    },
    SET_STATICS: (state, data) => {
      state.statics = data;
    },
    SET_DESIRE_CHAINID: (state, id) => {
      state.desireChainId = id;
      // 让只读 provider 跟着切链（否则换链后还在读上一条链的节点）
      setActiveChainId(id);
      // 持久化选择：刷新后仍停在这条链上（切换器会 reload，不持久化就白切了）
      try {
        if (id && Network[id]) {
          localStorage.setItem(CHAIN_STORAGE_KEY, id);
        }
      } catch (e) {
        /* ignore */
      }
    },
    // 应用主题（不持久化）：用于初始化、跟随系统、多标签同步
    SET_THEME: (state, theme) => {
      state.currentTheme = applyTheme(theme);
    },
    // 用户显式选择主题：应用 + 持久化 + 广播
    SET_USER_THEME: (state, theme) => {
      state.currentTheme = applyAndStoreTheme(theme);
    },
    /**
     * v2.4.0：后台下发的链顺序 / 默认链 / 测试网标记。
     * 接口拉不到时这些字段保持为空，前端完全沿用构建期配置（行为与以前一致）。
     */
    SET_CHAIN_CONFIG: (state, { order, defaultKey, testnetMap }) => {
      if (Array.isArray(order) && order.length) {
        state.chainOrder = order;
      }
      if (defaultKey) {
        state.serverDefaultKey = defaultKey;
      }
      if (testnetMap && typeof testnetMap === 'object') {
        state.testnetMap = { ...state.testnetMap, ...testnetMap };
      }
    },
    // add missing language mutation
    SET_LANG: (state, lang) => {
      state.lang = lang;
      localStorage.setItem('lang', lang);
      i18n.locale = lang;
      moment.locale(lang === 'zh' ? 'zh-cn' : 'en');
    },
    TOGGLE_DEVICE: (state, device) => {
      state.device = device;
    },
    SET_DesireChain: (state, value) => {
      state.desireChainId = value;
    },
  },
  actions: {
    /**
     * v2.4.0：启动时把「链顺序 + 默认链」对齐后台（/api/chains）。
     *
     * 以前链顺序写死在构建期的 VUE_APP_CHAINS 里，运营在后台改完顺序要重新
     * 构建前端才生效；现在启动拉一次：
     *   - 切链下拉按后台顺序展示（主网在前、测试网在后，组内按后台 sort）；
     *   - **新用户**（localStorage 里没有显式选过链）直接落到「排名第一的链」，
     *     也就是后台认定的默认链；老用户保留自己的选择，不被后台改动打断。
     */
    async initChainConfig({ commit }) {
      try {
        const res = await chainList({});
        const data = (res && res.data) || {};
        const chains = Array.isArray(data.chains) ? data.chains : [];
        if (!chains.length) {
          return;
        }

        // 后端按 evm chainId 下发，前端按注册表的 key 组织 —— 用 chainId 关联，
        // 不能用后端的枚举名（BSC 主网/测试网在后端都是枚举 BSC）。
        const keyByChainId = {};
        Object.values(CHAIN_REGISTRY).forEach((c) => {
          keyByChainId[Number(c.chainId)] = c.key;
        });

        const order = [];
        const testnetMap = {};
        chains.forEach((c) => {
          const chainId = Number(c.chain_id);
          testnetMap[chainId] = !!c.is_testnet;
          const key = keyByChainId[chainId];
          // 前端没启用这条链（VUE_APP_CHAINS 未包含）就跳过，保持本地可切换集合不变
          if (key && !order.includes(key)) {
            order.push(key);
          }
        });

        // 默认链：后端 default_chain（枚举值）→ 找不到就用列表第一条
        let defaultKey = '';
        const wanted = chains.find((c) => Number(c.chain) === Number(data.default_chain))
          || chains.find((c) => c.default)
          || chains[0];
        if (wanted) {
          defaultKey = keyByChainId[Number(wanted.chain_id)] || '';
        }

        commit('SET_CHAIN_CONFIG', { order, defaultKey, testnetMap });

        // 新用户才跟随默认链：读不到本地选择 = 还没选过
        let saved = null;
        try {
          saved = localStorage.getItem(CHAIN_STORAGE_KEY);
        } catch (e) {
          saved = null;
        }
        if (!saved && defaultKey && CHAIN_REGISTRY[defaultKey]) {
          const hex = toHexChainId(CHAIN_REGISTRY[defaultKey].chainId);
          if (Network[hex]) {
            // 用不持久化的 mutation：下次后台换默认链时，这个用户仍会跟随
            commit('SET_DesireChain', hex);
            setActiveChainId(hex);
          }
        }
      } catch (e) {
        // 接口不可用 → 沿用构建期配置（与 v2.3.1 完全一致的行为）
      }
    },
    /**
     * v2.5.0 资金风控：拉一次开关快照。
     * 提现 / 站内转 / 交易被后台紧急停掉时，前端据此置灰入口并给出原因，
     * 避免用户点了才看到后端报错。
     */
    async fetchRiskStatus({ commit }) {
      try {
        const res = await riskStatus({});
        if (res && res.code === 0 && res.data) {
          commit('SET_RISK', res.data);
          return res.data;
        }
      } catch (e) {
        // 接口不可用 → 保持默认（全开），不做任何禁用
      }
      return null;
    },

    async getUserInfo({ commit, getters }, account) {
      if (!account) {
        return;
      }
      if (loadingGetUserInfo) {
        return;
      }
      loadingGetUserInfo = true;
      try {
        let res = await getUserInfo(account, getters.chainType);
        // 只有拿到合法数组才覆盖，避免一次异常响应把页面上的余额清成空/0
        if (res && res.code == 0 && Array.isArray(res.data)) {
          commit('SET_FUNDData', res.data);
          commit('SET_STATICS', res.extra || {});
        }
      } catch (e) {
        console.log('getUserInfo error', e && e.message);
      } finally {
        // ⚠️ 必须放 finally：以前请求一抛异常这个开关就永久为 true，
        // 之后所有刷新（领取测试币后、下单后）都被静默丢弃 → 页面余额一直显示 0。
        loadingGetUserInfo = false;
      }
    },
    async updateAccount({ commit }, account) {
      commit('SET_ACCOUNT', account);
    },
    updateChainId({ commit }, id) {
      if (this.state.chainId == id) {
        return;
      }
      commit('SET_CHAINID', id);
    },
    updateDesireChainId({ commit }, id) {
      if (this.state.desireChainId == id) {
        return;
      }
      console.log('updateDesireChainId', id);
      commit('SET_DESIRE_CHAINID', id);
    },
    /**
     * 切换链（顶部切换器的唯一入口）
     * 1) 先持久化选择并让只读 provider 跟着切；
     * 2) 再让钱包切网络（钱包里没有这条链就自动加进去）；
     * 3) 成功后整页刷新 —— 本应用有大量链相关缓存（行情、持仓、资金、合约常量），
     *    局部刷新容易漏掉某个面板，刷新是最不会出错的做法（与 PancakeSwap 行为一致）。
     * 用户拒绝切网（4001）时回滚选择，页面保持原状。
     */
    async selectChain({ commit, dispatch, state }, key) {
      const chain = CHAIN_REGISTRY[key];
      if (!chain) {
        return false;
      }
      const hex = toHexChainId(chain.chainId);
      if (hex === state.desireChainId) {
        return true;
      }
      const prev = state.desireChainId;
      commit('SET_DESIRE_CHAINID', hex);
      const res = await ensureWalletChain(key);
      if (!res.ok) {
        commit('SET_DESIRE_CHAINID', prev);
        return false;
      }
      // 等钱包把 chainChanged 发完，再刷新，避免新旧链数据混在一张页面上
      window.setTimeout(() => window.location.reload(), res.skipped ? 0 : 400);
      return true;
    },
    updateTheme({ commit }, theme) {
      commit('SET_THEME', theme);
    },
    /**
     * 切换主题（全局唯一入口）。
     * 所有组件都应调用本 action，而不是各自操作 DOM —— 这是过去
     * "dark/light 切换不彻底" 的根因（多个组件各写一份 toggle 逻辑）。
     */
    toggleTheme({ commit }) {
      commit('SET_USER_THEME', toggleStoredTheme());
    },
    /** 同步来自其它标签页 / 系统偏好变化的主题（不改变用户显式选择） */
    syncTheme({ commit }, theme) {
      commit('SET_THEME', theme);
    },
    async connected({ commit }) {
      if (!window.ethereum) {
        message.error('install MetaMask first!');
        return;
      }
      let ethereum;
      if (window.ethereum.providers) {
        ethereum = window.ethereum.providers[0];
      } else {
        ethereum = window.ethereum;
      }
      try {
        let accounts = await ethereum.request({ method: 'eth_requestAccounts' });
        let account = accounts[0];
        commit('SET_ACCOUNT', account);
        return true;
      } catch (err) {
        console.log('connect error', err.message);
      }
      return false;
    },
    async switchNetwork({ state, commit, dispatch }, chainId) {
      if (chainId) {
        commit('SET_DESIRE_CHAINID', chainId);
      }
      if (!chainId) {
        chainId = state.desireChainId;
      }
      if (!window.ethereum) {
        return;
      }
      let ethereum;
      if (window.ethereum.providers) {
        ethereum = window.ethereum.providers[0];
      } else {
        ethereum = window.ethereum;
      }
      try {
        await ethereum.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId }],
        });
        commit('SET_CHAINID', chainId);
        if (!state.account) {
          dispatch('connected');
        }
        // window.location.reload();
        return true;
      } catch (switchError) {
        console.log('switchError', switchError.message);
        if (switchError.code == '4001') return;
        // This error code indicates that the chain has not been added to MetaMask.
        try {
          let fields = ['chainId', 'chainName', 'nativeCurrency', 'rpcUrls', 'blockExplorerUrls', 'iconUrls'];
          let obj = {};
          for (let i = 0; i < fields.length; i++) {
            let value = Network[chainId][fields[i]];
            // 空数组会让部分钱包直接报错（新链往往没有浏览器地址），不要传
            if (Array.isArray(value) && !value.length) {
              continue;
            }
            obj[fields[i]] = value;
          }
          let params = [obj];
          console.log(params);
          await ethereum.request({
            method: 'wallet_addEthereumChain',
            params,
          });
          if (!state.account) {
            let accounts = await ethereum.request({ method: 'eth_requestAccounts' });
            let account = accounts[0];
            commit('SET_ACCOUNT', account);
          }
          // window.location.reload();
          commit('SET_CHAINID', chainId);
          if (!state.account) {
            dispatch('connected');
          }
          return true;
        } catch (addError) {
          console.log('addError', addError.message);
        }
      }
      return false;
    },
  },
});
